import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
} from '@mui/material';

/**
 * Eén manier om de gebruiker feedback te geven, in plaats van alert(), window.confirm,
 * losse Snackbars per pagina en antd `message`.
 *
 * @example
 * const { notify, confirm } = useFeedback();
 * notify.success('Opgeslagen');
 * notify.error('Opslaan mislukt');
 * if (await confirm({ title: 'Document verwijderen?', message: 'Dit kan niet ongedaan worden gemaakt.', danger: true })) { ... }
 */
const FeedbackContext = createContext(null);

const AUTO_HIDE_MS = { success: 4000, info: 5000, warning: 7000, error: 8000 };

export function FeedbackProvider({ children }) {
  const [, setQueue] = useState([]);
  const [current, setCurrent] = useState(null);
  const [open, setOpen] = useState(false);
  const [dialog, setDialog] = useState(null);
  const idRef = useRef(0);
  // Of er nu een melding op het scherm staat (ref: houdt notify/confirm stabiel)
  const showingRef = useRef(false);

  const showNext = useCallback((pending) => {
    if (pending.length === 0) {
      showingRef.current = false;
      return pending;
    }
    const [next, ...rest] = pending;
    showingRef.current = true;
    setCurrent(next);
    setOpen(true);
    return rest;
  }, []);

  const push = useCallback(
    (severity, message) => {
      if (!message) return;
      const item = { id: ++idRef.current, severity, message: String(message) };
      setQueue((prev) => {
        const pending = [...prev, item];
        return showingRef.current ? pending : showNext(pending);
      });
    },
    [showNext],
  );

  const notify = useMemo(
    () => ({
      success: (message) => push('success', message),
      error: (message) => push('error', message),
      info: (message) => push('info', message),
      warning: (message) => push('warning', message),
    }),
    [push],
  );

  const handleClose = (_event, reason) => {
    if (reason === 'clickaway') return;
    setOpen(false);
  };

  const handleExited = () => {
    setCurrent(null);
    setQueue((prev) => showNext(prev));
  };

  const confirm = useCallback(
    ({
      title = 'Weet je het zeker?',
      message = '',
      confirmText = 'Bevestigen',
      cancelText = 'Annuleren',
      danger = false,
    } = {}) =>
      new Promise((resolve) => {
        setDialog({ title, message, confirmText, cancelText, danger, resolve });
      }),
    [],
  );

  const closeDialog = (result) => {
    dialog?.resolve(result);
    setDialog(null);
  };

  const value = useMemo(() => ({ notify, confirm }), [notify, confirm]);

  return (
    <FeedbackContext.Provider value={value}>
      {children}

      <Snackbar
        key={current?.id}
        open={open}
        autoHideDuration={AUTO_HIDE_MS[current?.severity] ?? 5000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        slotProps={{ transition: { onExited: handleExited } }}
      >
        {current ? (
          <Alert
            onClose={handleClose}
            severity={current.severity}
            variant="filled"
            sx={{ width: '100%', whiteSpace: 'pre-line' }}
          >
            {current.message}
          </Alert>
        ) : (
          <span />
        )}
      </Snackbar>

      <Dialog
        open={!!dialog}
        onClose={() => closeDialog(false)}
        aria-labelledby="feedback-confirm-title"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="feedback-confirm-title">{dialog?.title}</DialogTitle>
        {dialog?.message && (
          <DialogContent>
            <DialogContentText sx={{ whiteSpace: 'pre-line' }}>{dialog.message}</DialogContentText>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => closeDialog(false)}>{dialog?.cancelText}</Button>
          <Button
            onClick={() => closeDialog(true)}
            variant="contained"
            color={dialog?.danger ? 'error' : 'primary'}
            autoFocus
          >
            {dialog?.confirmText}
          </Button>
        </DialogActions>
      </Dialog>
    </FeedbackContext.Provider>
  );
}

export function useFeedback() {
  const context = useContext(FeedbackContext);
  if (!context) throw new Error('useFeedback moet binnen <FeedbackProvider> gebruikt worden');
  return context;
}
